import React, { useContext } from 'react';
import {
  OverlayContextProvider,
  OverlayContextProps,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import { MobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import {
  FacetStreamChildrenProps,
  FacetStreamProps,
} from '../FacetStream/types';
import { FacetPageType, Strip } from '../../../types/api';
import BrowseGrid from './BrowseGrid';
import BrowseTorpedo from './BrowseTorpedo';
import BrowseDuperContext from './BrowseDuperContext';
import { BrowseLayout } from '../../../constants';
import PagingWrapper from './PagingWrapper';
import { BiLoggerContextProvider } from '@wix/da-shared-react/pkg/biLogger';
import BrowseFeed from './BrowseFeed';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks';
import noop from '@wix/da-shared-react/pkg/utils/noop';

export interface Props extends FacetStreamProps {
  pageType: FacetPageType;
  layout: BrowseLayout;
  isInfiniteScroll?: boolean;
  isDuperbrowseActive: boolean;
  /** The title used in duperbrowse's "back to <title>" string */
  duperbrowseTitle?: string;
  strips?: Strip[];
  contentStrip?: React.ReactNode;
  contentStripIndex?: number;
  contentStripHeight?: number;
  effectiveOrder: string;
  showEndOfResults?: boolean;
  caughtUpIndex?: number;
  markCurrentPageAsSeen: () => void;
  onOffsetReached: (offset: number) => void;
}

const BrowseThumbLayout: React.FC<Props & FacetStreamChildrenProps> = ({
  isDuperbrowseActive,
  duperbrowseTitle,
  items,
  onOffsetReached,
  pageType,
  isInfiniteScroll,
  streamId,
  strips,
  contentStrip,
  contentStripIndex,
  contentStripHeight,
  layout,
  effectiveOrder,
  showEndOfResults,
  caughtUpIndex,
  markCurrentPageAsSeen,
}) => {
  const isMobile = useContext(MobileContext);
  const overlayContextProps: OverlayContextProps = { showAuthorTooltip: true };
  const newContentMarkers = useFlag('new_content_separator');

  return (
    <BiLoggerContextProvider value={{ sort_type: effectiveOrder }}>
      <OverlayContextProvider value={overlayContextProps}>
        <PagingWrapper streamId={streamId} showEndOfResults={showEndOfResults}>
          <BrowseDuperContext
            items={items}
            streamId={streamId}
            pageType={pageType}
            parentTitle={duperbrowseTitle}
            isLoadMoreAllowed={isInfiniteScroll}
          >
            {layout === BrowseLayout.Torpedo && (
              <BrowseTorpedo
                strips={strips}
                items={items}
                isDuperbrowseActive={isDuperbrowseActive}
                isMobile={isMobile}
                onOffsetReached={onOffsetReached}
                streamId={streamId}
                isInfiniteScroll={isInfiniteScroll}
                caughtUpIndex={caughtUpIndex}
                markCurrentPageAsSeen={
                  newContentMarkers ? markCurrentPageAsSeen : noop
                }
              />
            )}
            {layout === BrowseLayout.Grid && (
              <BrowseGrid
                strips={strips}
                items={items}
                pageType={pageType}
                isMobile={isMobile}
                contentStrip={contentStrip}
                contentStripIndex={contentStripIndex}
                contentStripHeight={contentStripHeight}
                isInfiniteScroll={isInfiniteScroll}
                onOffsetReached={onOffsetReached}
              />
            )}
            {layout === BrowseLayout.Feed && (
              <BrowseFeed
                items={items}
                strips={strips}
                caughtUpIndex={caughtUpIndex}
                markCurrentPageAsSeen={markCurrentPageAsSeen}
              />
            )}
          </BrowseDuperContext>
        </PagingWrapper>
      </OverlayContextProvider>
    </BiLoggerContextProvider>
  );
};
BrowseThumbLayout.displayName = 'BrowseThumbLayout';

export default BrowseThumbLayout;
